import { useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import DataTable from "@components/DataTable";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { useNavigate } from "react-router";
import {
  delta,
  formatDeliveryDate,
  hours,
  variancePercent,
} from "@common/estimateAccuracy";
import useListProjectReviews from "../api/useListProjectReviews";
import { ProjectReview } from "../types";
import { useStyles } from "./styles";

const EMPTY_MESSAGE =
  "No projects have completed delivery yet. A project appears here once one of its services closes in your PSA.";

const psaGap = (count: number) =>
  `${count} service${count === 1 ? "" : "s"} missing PSA data`;

const ProjectReviewsIndex = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [sortBy, setSortBy] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [errorDismissed, setErrorDismissed] = useState<boolean>(false);

  const { reviews, isLoading, error, pageMeta } = useListProjectReviews({
    pageNumber,
    pageSize,
    sortBy,
    sortOrder,
  });

  const onSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
    setPageNumber(1);
  };

  // Only the two columns the API can order in SQL are sortable. The rest are
  // plain headers rather than controls that look live and do nothing.
  const sortableHeader = (label: string, column: string) => (
    <div
      className={styles.sortableColumnHeader}
      onClick={() => onSort(column)}
      role="button"
    >
      {label}
    </div>
  );

  const header = (label: string) => (
    <div className={styles.columnHeader}>{label}</div>
  );

  const columns = [
    {
      // camelCase, unlike the API sort key passed to onSort below: DataTable
      // builds each th's class from the column id, and the width rule in
      // styles/useStyles.ts targets `.projectNameCol`.
      id: "projectName",
      header: () => sortableHeader("Project", "project-name"),
      cell(props) {
        const review: ProjectReview = props.row.original;
        return (
          <div
            className={styles.projectLink}
            role="button"
            onClick={() => navigate(`/project_reviews/${review.id}`)}
          >
            {review.projectName}
          </div>
        );
      },
    },
    {
      id: "client-name",
      header: () => header("Client"),
      cell: (props) => props.row.original.clientName,
    },
    {
      id: "last-completed-on",
      header: () => sortableHeader("Last Completed", "last-completed-on"),
      cell: (props) => formatDeliveryDate(props.row.original.lastCompletedOn),
    },
    {
      id: "settled",
      header: () => header("Settled"),
      cell(props) {
        const review: ProjectReview = props.row.original;
        return (
          <div>
            <div>{`${review.servicesSettled} of ${review.servicesTracked}`}</div>
            {review.unsettledServices > 0 && (
              <div className={styles.caveat}>
                Hours cover settled services only
              </div>
            )}
            {/* A partially-recorded service still counts as settled, so
                without this the row reads as complete and on budget. */}
            {review.servicesPartial > 0 && (
              <div className={styles.caveat}>
                {psaGap(review.servicesPartial)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: "estimated-hours",
      header: () => header("Sold Hrs"),
      cell: (props) => hours(props.row.original.estimatedHours),
    },
    {
      id: "actual-hours",
      header: () => header("Actual Hrs"),
      cell: (props) => hours(props.row.original.actualHours),
    },
    {
      id: "delta-hours",
      header: () => header("Delta"),
      cell: (props) => delta(props.row.original.deltaHours),
    },
    {
      id: "variance-percentage",
      header: () => header("Variance"),
      cell: (props) => variancePercent(props.row.original.variancePercentage),
    },
  ];

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <div className={styles.wrapper}>
      {error && !errorDismissed && (
        <ToastAlert
          variant="danger"
          text="Could not load project reviews."
          onClose={() => setErrorDismissed(true)}
        />
      )}
      <Row>
        <Col>
          <h1 className={styles.title}>Project Review</h1>
        </Col>
        <hr className={styles.line} />
      </Row>
      {/* A failed request returns zero rows too, and the empty state would
          report "nothing has been delivered yet" when the truth is that we do
          not know. While the toast is saying so, the card has nothing to hold
          and is left out rather than rendered as an empty white box; once the
          toast autohides (4s), the inline fallback below takes over. */}
      {(!error || errorDismissed) && (
        <Row>
          <Col>
            <Card className={styles.card}>
              {error ? (
                <p className={styles.empty}>Could not load project reviews.</p>
              ) : reviews.length === 0 ? (
                <p className={styles.empty}>{EMPTY_MESSAGE}</p>
              ) : (
                <DataTable
                  data={reviews}
                  columns={columns}
                  striped
                  hover
                  paginationEnabled
                  pageSizeEnabled
                  currentPage={pageNumber}
                  setCurrentPage={setPageNumber}
                  pageSize={pageSize}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPageNumber(1);
                  }}
                  totalRows={pageMeta.recordCount}
                  totalPages={pageMeta.pageCount}
                />
              )}
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default ProjectReviewsIndex;
